import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, keys, flatten, compact, startCase } from 'lodash';
import ActivityLogList from '../ActivityLogList';
import styles from '../ActivityLogList/ActivityLogList.style';
import { formatDateString } from '../../utils';

const editingOperations = ['approvedEdits', 'edited', 'rejectedEdits'];
const attachmentOperations = ['uploaded', 'removed', 'submit-for-approval', 'approve', 'reject'];

const operationMessages = {
  approved: 'approved',
  approvedEdits: 'approved edits to',
  created: 'created',
  edited: 'made edits to',
  rejectedEdits: 'rejected edits to',
  rejected: 'rejected',
  submitted: 'submitted',
  updated: 'updated',
  uploaded: 'uploaded',
  removed: 'removed',
  'submit-for-approval': 'submitted',
  approve: 'approved & shared',
  reject: 'rejected',
  submittedEdits: 'submitted',
  pause: 'paused',
};

const operationsNotShowForPartner = ['uploaded', 'removed', 'submit-for-approval', 'reject'];

const detailsToBeAdded = ['contractCurrency', 'paymentCurrency'];

const getUpdatedItems = (updated, newData) => {
  const updatedDetails = keys(updated)
    .filter(property => detailsToBeAdded.includes(property))
    .map(property => {
      const effectiveDate = `${property}EffectiveDate`;
      return {
        value: (
          <p>
            {startCase(property)} changed from
            <b className={styles.bold}> {newData[property]} </b> to
            <b className={styles.bold}>
              {' '}
              {newData?.pendingUpdates && newData?.pendingUpdates[property]}{' '}
            </b>{' '}
            with effective date
            <b className={styles.bold}>
              {' '}
              {formatDateString(newData?.pendingUpdates[effectiveDate])}
            </b>
          </p>
        ),
      };
    });
  return updatedDetails;
};

const formatActivityLog = (item, resourceName, isPartner, previousActivity) => {
  const { _id, operation, initiatedBy, newData, params } = item;
  const displayedResourceName = resourceName || newData?.name;
  const changeDetails = [];
  let actionMessageEnding;
  const bulletPoints = [];

  let operationType = Object.keys(operationMessages).find(o => o === operation);
  if (operation === 'updated' || operation === 'submittedChanges') operationType = 'edited';
  if (operationType === 'edited') return null;
  if (isPartner && operationsNotShowForPartner.includes(operation)) return null;

  // // add sanity check for `oldData` to prevent errors on corrupted data
  if (
    !isEmpty(previousActivity?.detailedDiff?.updated?.pendingUpdates) ||
    (!isEmpty(previousActivity?.detailedDiff?.added?.pendingUpdates) &&
      operation === 'submittedEdits')
  ) {
    const pendingUpdates = !isEmpty(previousActivity?.detailedDiff?.added?.pendingUpdates)
      ? previousActivity?.detailedDiff?.added?.pendingUpdates
      : previousActivity?.detailedDiff?.updated?.pendingUpdates;
    const updatedDetails = getUpdatedItems(pendingUpdates, previousActivity?.newData);
    changeDetails.push(flatten(compact(updatedDetails)));
  }

  const actionMessage = operationMessages[operationType];
  let areaMessage;
  if (editingOperations.includes(operationType)) {
    areaMessage = 'contract terms';
  } else if (attachmentOperations.includes(operationType)) {
    // need to update
    areaMessage = 'attachment(s)';
  } else {
    areaMessage = displayedResourceName || 'contract terms and royalty fees';
  }

  switch (operationType) {
    case 'submitted':
    case 'submittedEdits':
      actionMessageEnding = 'for review';
      bulletPoints.push({
        title: 'Reviewer',
        value: newData?.reviewer?.displayName || 'Reviewer not provided',
      });
      bulletPoints.push({
        title: 'Reviewer Justification',
        value: newData?.reviewerJustification || 'Justification not provided',
      });
      if (!isEmpty(changeDetails)) {
        changeDetails.forEach(activity => bulletPoints.push(...activity));
      }
      break;
    case 'rejected':
      bulletPoints.push({
        title: 'Response',
        value: params?.rejectReason || 'Rejection reason not provided',
      });
      break;
    default:
  }

  return {
    _id,
    actionMessage,
    areaMessage,
    actionMessageEnding,
    bulletPoints,
    initiatedBy,
    resourceName,
    timestamp: item.timestamp,
  };
};

const formattedActivityLogs = (items, resourceName, isPartner) => {
  const result = [];

  items.forEach((item, index) => {
    const previousActivity = index !== items.length ? items[index + 1] : null;
    const formattedActivityLog = formatActivityLog(item, resourceName, isPartner, previousActivity);
    if (formattedActivityLog) result.push(formattedActivityLog);
  });

  return result;
};

const ContractActivity = ({ items, resourceName, isPartner = false }) => {
  return (
    <ActivityLogList
      title="Contract Activity"
      formattedActivityLogs={formattedActivityLogs(items, resourceName, isPartner)}
    />
  );
};

// same props as v1
ContractActivity.propTypes = {
  resourceName: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
      initiatedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
      operation: PropTypes.string.isRequired,
      area: PropTypes.string.isRequired,
      newData: PropTypes.shape({
        address: PropTypes.string,
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
        contractId: PropTypes.string,
      }),
      oldData: PropTypes.shape({
        name: PropTypes.string,
        rate: PropTypes.number,
        calculation: PropTypes.string,
        effectiveDate: PropTypes.string,
        terminationDate: PropTypes.string,
        platform: PropTypes.string,
      }),
      params: PropTypes.shape({
        contractAddress: PropTypes.string,
      }),
    }),
  ),
  isPartner: PropTypes.bool,
};

ContractActivity.defaultProps = {
  resourceName: '',
  items: [],
  isPartner: false,
};

export default ContractActivity;
